import { gameSlots, resolveSlot, type ScheduledGame, type Slot, type Team } from "./types";

/**
 * Bracket graph maths, shared by the editor and the public board.
 *
 * Both surfaces render the same bracket, so both must agree on where a team
 * can go and where every box sits. That agreement is only guaranteed if there
 * is one implementation: the board used to carry its own round-column
 * fallback, which drifted from what the editor drew.
 *
 * Everything here is pure and tested. Interaction is not testable; this is.
 */

/** Depth ceiling for every walk. A hand-wired bracket can contain a cycle. */
const MAX_DEPTH = 32;

export interface SlotCtx {
  teams: Team[];
  schedule: ScheduledGame[];
  seedsOf: (divisionId: string, poolId?: string) => string[];
}

/**
 * Where one team can be found in a bracket, split by certainty.
 *
 *   confirmed: the team's presence is SETTLED. Games it has played, plus the
 *               game it has already won its way into. This is the route it
 *               actually took, and where it is headed next.
 *   possible : reachable but undecided. From any game the team is in but has
 *               not played, BOTH the winner and the loser branch stay open, so
 *               both are followed to the ends.
 *
 * Knocked out falls out of the same rule rather than needing a special case:
 * in single elimination nothing consumes that `loser`, so the branch simply
 * ends; in double elimination the losers bracket consumes it, so the team's
 * possible routes continue there, which is exactly right.
 */
export interface TeamPaths {
  confirmed: Set<string>;
  possible: Set<string>;
}

export function emptyPaths(): TeamPaths {
  return { confirmed: new Set(), possible: new Set() };
}

/**
 * Which side of a game a team occupies, or null when it does not (yet).
 *
 * Resolution goes through `resolveSlot`, so a team only counts as present once
 * that is actually determined: a `seed` slot before pool play finishes resolves
 * to nothing, and the team correctly has no path rather than a guessed one.
 */
function sideOf(game: ScheduledGame, teamId: string, ctx: SlotCtx): "a" | "b" | null {
  const { a, b } = gameSlots(game);
  if (resolveSlot(a, ctx)?.id === teamId) return "a";
  if (resolveSlot(b, ctx)?.id === teamId) return "b";
  return null;
}

/** Games consuming a given branch of `gameId`: its downstream continuations. */
function consumers(
  games: readonly ScheduledGame[],
  gameId: string,
  kind: "winner" | "loser",
): ScheduledGame[] {
  return games.filter((g) => {
    const { a, b } = gameSlots(g);
    return [a, b].some((s: Slot | null) => s !== null && s.kind === kind && s.gameId === gameId);
  });
}

/**
 * The route(s) one team has through a bracket.
 *
 * Only bracket-stage games are walked: pool play is a table, not a tree, and
 * including it would light up every game a team ever played.
 */
export function teamPaths(
  schedule: readonly ScheduledGame[],
  teamId: string,
  ctx: SlotCtx,
): TeamPaths {
  const games = schedule.filter((g) => g.stage === "bracket");
  const confirmed = new Set<string>();
  const possible = new Set<string>();
  if (teamId === "") return { confirmed, possible };

  /**
   * Everything downstream of an undecided game is undecided, so once a walk
   * turns speculative it stays speculative. `seen` is keyed by certainty as
   * well as id: a game first reached as "possible" must still be upgraded if
   * it is later reached as "confirmed".
   */
  const seen = new Set<string>();
  const walk = (gameId: string, certain: boolean, depth: number): void => {
    if (depth > MAX_DEPTH) return;
    const key = `${gameId}:${certain ? "c" : "p"}`;
    if (seen.has(key)) return;
    seen.add(key);

    const game = games.find((g) => g.id === gameId);
    if (game === undefined) return;
    (certain ? confirmed : possible).add(gameId);

    const result = game.result;
    if (certain && result !== undefined && result.scoreA !== result.scoreB) {
      // Played and decided: only the branch this team actually took continues,
      // and the team's presence in that next game is settled too.
      const side = sideOf(game, teamId, ctx);
      if (side === null) return;
      const won = side === "a" ? result.scoreA > result.scoreB : result.scoreB > result.scoreA;
      for (const next of consumers(games, gameId, won ? "winner" : "loser")) {
        walk(next.id, true, depth + 1);
      }
      return;
    }

    // Unplayed (or drawn, which decides nothing): both branches stay open and
    // everything beyond this point is speculative.
    for (const kind of ["winner", "loser"] as const) {
      for (const next of consumers(games, gameId, kind)) walk(next.id, false, depth + 1);
    }
  };

  for (const game of games) {
    if (sideOf(game, teamId, ctx) !== null) walk(game.id, true, 0);
  }

  // A game reached both ways is confirmed; drop the weaker claim so the two
  // sets never overlap and the UI never has to pick between them.
  for (const id of confirmed) possible.delete(id);
  return { confirmed, possible };
}

/**
 * Teams that can be highlighted in this bracket: everyone resolvable in it.
 * Used to decide whether a side is clickable at all.
 */
export function teamsInBracket(schedule: readonly ScheduledGame[], ctx: SlotCtx): Set<string> {
  const out = new Set<string>();
  for (const game of schedule) {
    if (game.stage !== "bracket") continue;
    const { a, b } = gameSlots(game);
    for (const slot of [a, b]) {
      const team = resolveSlot(slot, ctx);
      if (team !== null) out.add(team.id);
    }
  }
  return out;
}

// ---------- layout ----------

/** Box geometry. The generators draw on this grid too (`shared/generate.ts`). */
export const BOX_W = 190;
/**
 * Box metrics, PINNED against bracket-view.css: the CSS gives the header and
 * each side row these exact heights, and the wire endpoints below are derived
 * from them - one set of numbers, so the SVG geometry cannot drift from the
 * DOM again. It did once: the box was declared 76px with endpoint fractions
 * eyeballed at 0.32/0.72, then the side rows grew a 32px tap floor, the real
 * card became ~88px tall, and every incoming wire landed about half a row
 * above the slot it fed.
 */
export const BOX_BORDER = 1;
export const BOX_HEAD_H = 24;
export const BOX_SIDE_H = 33;
export const BOX_H = 2 * BOX_BORDER + BOX_HEAD_H + 2 * BOX_SIDE_H;
/** Vertical centers of side A / side B rows, as fractions of the box. Used by
 *  the wires (both ends) and by the W/L port buttons, so everything meets on
 *  the same line. */
export const SIDE_A_Y = (BOX_BORDER + BOX_HEAD_H + BOX_SIDE_H / 2) / BOX_H;
export const SIDE_B_Y = (BOX_BORDER + BOX_HEAD_H + BOX_SIDE_H * 1.5) / BOX_H;
const COL_W = 280;
const ROW_H = BOX_H + 26;

export interface Point {
  x: number;
  y: number;
}

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * Position for every game, authored where the organiser dragged it and derived
 * where they have not. One function so the editor and the board can never
 * disagree; the board previously had its own round-column fallback.
 *
 * Games with no position are laid out by `bracketRound`, which is what an
 * untouched generated bracket already implies.
 */
export function bracketLayout(games: readonly ScheduledGame[]): Map<string, Point> {
  const out = new Map<string, Point>();
  const undrawn: ScheduledGame[] = [];
  for (const g of games) {
    if (g.x !== undefined && g.y !== undefined) out.set(g.id, { x: g.x, y: g.y });
    else undrawn.push(g);
  }

  // Derived boxes are stacked per round, below anything already drawn, so a
  // half-arranged bracket does not pile new boxes on top of placed ones.
  const top =
    out.size === 0 ? 40 : [...out.values()].reduce((m, p) => Math.max(m, p.y + BOX_H + 40), 40);
  const perRound = new Map<number, number>();
  for (const g of [...undrawn].sort((a, b) => a.startAt.localeCompare(b.startAt))) {
    const round = g.bracketRound ?? 1;
    const row = perRound.get(round) ?? 0;
    perRound.set(round, row + 1);
    out.set(g.id, { x: 40 + (round - 1) * COL_W, y: top + row * ROW_H });
  }
  return out;
}

/** Bounding box of a laid-out bracket, with a little air around it. */
export function bracketBounds(layout: Map<string, Point>, pad = 40): Bounds {
  if (layout.size === 0) return { minX: 0, minY: 0, maxX: 1, maxY: 1 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of layout.values()) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x + BOX_W);
    maxY = Math.max(maxY, p.y + BOX_H);
  }
  return { minX: minX - pad, minY: minY - pad, maxX: maxX + pad, maxY: maxY + pad };
}

/** Where a game's winner/loser wire leaves its box. */
export function portPoint(at: Point, kind: "winner" | "loser"): Point {
  return { x: at.x + BOX_W, y: at.y + BOX_H * (kind === "winner" ? SIDE_A_Y : SIDE_B_Y) };
}

/** Where a wire lands on the receiving side of a box. */
export function sidePoint(at: Point, side: "a" | "b"): Point {
  return { x: at.x, y: at.y + BOX_H * (side === "a" ? SIDE_A_Y : SIDE_B_Y) };
}

/** Horizontal cubic bezier: reads as a bracket connector rather than a wire. */
export function wirePath(from: Point, to: Point): string {
  const dx = Math.max(36, Math.abs(to.x - from.x) * 0.5);
  return `M ${from.x} ${from.y} C ${from.x + dx} ${from.y}, ${to.x - dx} ${to.y}, ${to.x} ${to.y}`;
}

/**
 * Whether wiring `fromId`'s outcome into `toId` would close a loop.
 *
 * A game cannot feed itself, and a longer loop (A → B → A) is just as dead:
 * every slot on it resolves to null forever, silently. Walk what the source is
 * fed BY; if the target is upstream, the wire would close the loop.
 */
export function wouldCycle(
  games: readonly ScheduledGame[],
  fromId: string,
  toId: string,
): boolean {
  if (fromId === toId) return true;
  const upstream = new Set<string>();
  const walk = (id: string, depth: number): void => {
    if (depth > MAX_DEPTH) return;
    const g = games.find((x) => x.id === id);
    if (g === undefined) return;
    const { a, b } = gameSlots(g);
    for (const slot of [a, b]) {
      if (slot === null || (slot.kind !== "winner" && slot.kind !== "loser")) continue;
      if (upstream.has(slot.gameId)) continue;
      upstream.add(slot.gameId);
      walk(slot.gameId, depth + 1);
    }
  };
  walk(fromId, 0);
  return upstream.has(toId);
}

// ---------- wiring rules ----------

export interface WireTarget {
  gameId: string;
  side: "a" | "b";
}

/**
 * What wiring an outcome into a side should actually do.
 *
 * Two things are structurally impossible in a bracket and the editor used to
 * allow both, because the drop path had no rules at all:
 *
 *   · **A team cannot play itself.** Both sides of one game holding the same
 *     outcome ("Winner Play-in" v "Winner Play-in") is not a bracket.
 *   · **An outcome has ONE destination.** A team plays exactly one next game,
 *     so the winner of a play-in cannot feed two semifinals: whichever it
 *     reaches, it is not in the other.
 *
 * The first is refused. The second MOVES the wire rather than refusing, since
 * re-routing is the common intent and hunting down the old target to clear it
 * first is pure friction, but the move is reported, never silent.
 */
export type WirePlan =
  | { ok: false; reason: "cycle" | "self" }
  | { ok: true; set: WireTarget; clear: WireTarget[] };

/**
 * Every side an outcome currently lands on.
 *
 * Plural because a schedule edited before this rule existed can already hold
 * duplicates: the editor accepted four wires out of one play-in. Clearing all
 * of them on the next re-route makes the rule self-healing instead of leaving
 * the organiser to hunt the extras down one drag at a time.
 */
export function outcomeDestinations(
  games: readonly ScheduledGame[],
  fromId: string,
  kind: "winner" | "loser",
): WireTarget[] {
  const out: WireTarget[] = [];
  for (const g of games) {
    const { a, b } = gameSlots(g);
    if (a !== null && a.kind === kind && a.gameId === fromId) out.push({ gameId: g.id, side: "a" });
    if (b !== null && b.kind === kind && b.gameId === fromId) out.push({ gameId: g.id, side: "b" });
  }
  return out;
}

export function planWire(
  games: readonly ScheduledGame[],
  fromId: string,
  kind: "winner" | "loser",
  to: WireTarget,
): WirePlan {
  if (wouldCycle(games, fromId, to.gameId)) return { ok: false, reason: "cycle" };

  // Would this put the same outcome on both sides of the target?
  const target = games.find((g) => g.id === to.gameId);
  if (target !== undefined) {
    const other = gameSlots(target)[to.side === "a" ? "b" : "a"];
    if (other !== null && other.kind === kind && other.gameId === fromId) {
      return { ok: false, reason: "self" };
    }
  }

  // Everything this outcome currently feeds, except the side it is landing on
  // (which is about to be overwritten anyway).
  const clear = outcomeDestinations(games, fromId, kind).filter(
    (d) => !(d.gameId === to.gameId && d.side === to.side),
  );
  return { ok: true, set: to, clear };
}
